import { action, computed, makeObservable, observable } from 'mobx';

import { apiUrls } from 'shared/entities/domain';
import {
  MANAGER_SEGMENT_OTHER_TEXT_MAX_LENGTH,
  MANAGER_SEGMENT_QUESTIONS,
  ManagerSegment,
  IManagerSegmentAnswer,
  ManagerSegmentAnswerValue,
  IManagerSegmentComplete,
  ManagerSegmentQuestion,
  IManagerSegmentQuestionDefinition,
  ManagerSegmentCompleteServer,
  ManagerSegmentSelectionMode,
  ManagerSegmentStep,
  getManagerSegmentTextLength,
  normalizeManagerSegmentComplete
} from 'shared/entities/managerSegment';
import { IRootStore } from 'shared/entities/store/rootStore';
import { FieldModel } from 'shared/models/form';
import { Validator } from 'shared/entities/validator';
import { LoadingStageModel } from 'shared/models/loadingStage';
import { OpenStateModel } from 'shared/models/openState';

const OTHER_TEXT_REQUIRED_ERROR = (t) =>
  t('managerSegment.validation.otherRequired', { ns: 'app' });

const OTHER_TEXT_MAX_LENGTH_ERROR = (t) =>
  t('managerSegment.validation.otherMaxLength', {
    ns: 'app',
    count: MANAGER_SEGMENT_OTHER_TEXT_MAX_LENGTH
  });

/** Единственное место, где живёт правило для текста «другое» */
const otherTextValidator: Validator<string> = (value) => {
  const length = getManagerSegmentTextLength(value.trim());

  if (length === 0) {
    return OTHER_TEXT_REQUIRED_ERROR;
  }

  return length > MANAGER_SEGMENT_OTHER_TEXT_MAX_LENGTH
    ? OTHER_TEXT_MAX_LENGTH_ERROR
    : null;
};

export default class ManagerSegmentStore {
  readonly rootStore: IRootStore;
  readonly modalState = new OpenStateModel();
  readonly segment = new FieldModel<ManagerSegment | null>(null);
  readonly segmentOtherText = new FieldModel('', [otherTextValidator]);
  readonly taskOtherText = new FieldModel('', [otherTextValidator]);
  readonly skipStage = new LoadingStageModel();
  readonly completeStage = new LoadingStageModel();

  step: ManagerSegmentStep = ManagerSegmentStep.segment;
  questionIndex: number = 0;
  answers: Map<ManagerSegmentQuestion, ManagerSegmentAnswerValue[]> = new Map();

  private managerId: string | null = null;
  private abortController: AbortController | null = null;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;

    makeObservable<
      ManagerSegmentStore,
      '_resetForm' | '_finishSkip' | '_showSuccess'
    >(this, {
      step: observable,
      questionIndex: observable,
      answers: observable,

      questions: computed,
      currentQuestion: computed,
      currentValues: computed,
      totalQuestions: computed,
      isLastQuestion: computed,
      isSubmitting: computed,
      canContinue: computed,
      canAdvanceQuestion: computed,
      canComplete: computed,
      isOtherSelected: computed,

      sync: action,
      reset: action,
      selectSegment: action,
      selectSingleAnswer: action,
      toggleMultipleAnswer: action,
      changeSegmentOtherText: action,
      changeTaskOtherText: action,
      goToQuestions: action,
      goToNextQuestion: action,
      goToPreviousQuestion: action,
      skip: action,
      complete: action,
      acknowledgeSuccess: action,
      close: action,
      _resetForm: action,
      _finishSkip: action,
      _showSuccess: action
    });
  }

  get questions(): IManagerSegmentQuestionDefinition[] {
    return this.segment.value
      ? MANAGER_SEGMENT_QUESTIONS[this.segment.value]
      : [];
  }

  get currentQuestion(): IManagerSegmentQuestionDefinition | null {
    return this.questions[this.questionIndex] || null;
  }

  get currentValues(): ManagerSegmentAnswerValue[] {
    return this.currentQuestion
      ? this.answers.get(this.currentQuestion.question) || []
      : [];
  }

  get totalQuestions(): number {
    return this.questions.length;
  }

  get isLastQuestion(): boolean {
    return (
      this.totalQuestions > 0 && this.questionIndex === this.totalQuestions - 1
    );
  }

  get isSubmitting(): boolean {
    return this.skipStage.isLoading || this.completeStage.isLoading;
  }

  get canContinue(): boolean {
    return Boolean(this.segment.value) && this.isSegmentOtherTextValid;
  }

  get canAdvanceQuestion(): boolean {
    return this.isCurrentTaskOtherTextValid && !this.isSubmitting;
  }

  get canComplete(): boolean {
    return this.canContinue && this.isTaskOtherTextValid;
  }

  /** Текст «другое» на шаге сегмента обязателен только для сегмента other */
  private get isSegmentOtherRequired(): boolean {
    return this.segment.value === ManagerSegment.other;
  }

  /** Текст «другое» по задачам обязателен, если вариант other выбран хоть в одном вопросе */
  private get isTaskOtherRequired(): boolean {
    return Array.from(this.answers.values()).some((values) =>
      values.includes(ManagerSegmentAnswerValue.other)
    );
  }

  /** Выбран ли вариант «другое» в текущем вопросе: от этого зависит и поле, и валидация */
  get isOtherSelected(): boolean {
    return this.currentValues.includes(ManagerSegmentAnswerValue.other);
  }

  private get isSegmentOtherTextValid(): boolean {
    return (
      !this.isSegmentOtherRequired ||
      this.isOtherTextValid(this.segmentOtherText.value)
    );
  }

  private get isTaskOtherTextValid(): boolean {
    return (
      !this.isTaskOtherRequired ||
      this.isOtherTextValid(this.taskOtherText.value)
    );
  }

  private get isCurrentTaskOtherTextValid(): boolean {
    return (
      !this.isOtherSelected || this.isOtherTextValid(this.taskOtherText.value)
    );
  }

  private isOtherTextValid(value: string): boolean {
    return otherTextValidator(value) === null;
  }

  sync = (managerId: string, showSegment: boolean): void => {
    if (this.managerId !== managerId) {
      this.reset();
      this.managerId = managerId;
    }

    if (showSegment) {
      this.modalState.open();
      return;
    }

    this.modalState.close();
    this._resetForm();
  };

  reset = (): void => {
    this.abortController?.abort();
    this.managerId = null;
    this.modalState.close();
    this._resetForm();
  };

  selectSegment = (segment: ManagerSegment): void => {
    if (this.segment.value === segment) {
      return;
    }

    this.segment.changeValue(segment);
    this.questionIndex = 0;
    this.answers.clear();
    this.segmentOtherText.changeValue('');
    this.segmentOtherText.resetTouched();
    this.taskOtherText.changeValue('');
    this.taskOtherText.resetTouched();
  };

  selectSingleAnswer = (value: ManagerSegmentAnswerValue): void => {
    const question = this.currentQuestion;
    if (
      !question ||
      question.selectionMode !== ManagerSegmentSelectionMode.single ||
      !question.values.includes(value)
    ) {
      return;
    }

    this.answers.set(question.question, [value]);
  };

  toggleMultipleAnswer = (value: ManagerSegmentAnswerValue): void => {
    const question = this.currentQuestion;
    if (
      !question ||
      question.selectionMode !== ManagerSegmentSelectionMode.multiple ||
      !question.values.includes(value)
    ) {
      return;
    }

    const currentValues = this.answers.get(question.question) || [];
    const values = currentValues.includes(value)
      ? currentValues.filter((item) => item !== value)
      : [...currentValues, value];
    this.answers.set(question.question, values);

    if (!this.isTaskOtherRequired) {
      this.taskOtherText.changeValue('');
      this.taskOtherText.resetTouched();
    }
  };

  changeSegmentOtherText = (value: string): void => {
    this.segmentOtherText.changeValue(value);
  };

  changeTaskOtherText = (value: string): void => {
    this.taskOtherText.changeValue(value);
  };

  goToQuestions = (): void => {
    if (!this.validateSegmentStep()) {
      return;
    }

    this.step = ManagerSegmentStep.questions;
  };

  goToNextQuestion = (): void => {
    if (this.isLastQuestion || !this.validateCurrentTaskOtherText()) {
      return;
    }

    this.questionIndex += 1;
  };

  goToPreviousQuestion = (): void => {
    if (this.questionIndex === 0) {
      this.step = ManagerSegmentStep.segment;
      return;
    }

    this.questionIndex -= 1;
  };

  skip = async (): Promise<void> => {
    const submitted = await this._submit(
      this.skipStage,
      apiUrls.MANAGERS_SEGMENT_SKIP
    );

    if (submitted) {
      this._finishSkip();
      this.rootStore.userStore.markSegmentHandled();
    }
  };

  complete = async (): Promise<void> => {
    if (this.isSubmitting || !this.isLastQuestion || !this.validateComplete()) {
      return;
    }

    const segment = this.segment.value;
    if (!segment) {
      return;
    }

    const value: IManagerSegmentComplete = {
      segment,
      answers: this.buildAnswers(),
      segmentOtherText: this.segmentOtherText.value,
      taskOtherText: this.taskOtherText.value
    };

    const submitted = await this._submit(
      this.completeStage,
      apiUrls.MANAGERS_SEGMENT_COMPLETE,
      normalizeManagerSegmentComplete(value)
    );

    if (submitted) {
      this._showSuccess();
    }
  };

  close = (): void => {
    this.modalState.close();
    this._resetForm();
    this.rootStore.userStore.markSegmentHandled();
  };

  acknowledgeSuccess = (): void => {
    if (this.step !== ManagerSegmentStep.success) {
      return;
    }

    this.modalState.close();
    this._resetForm();
    this.rootStore.userStore.markSegmentHandled();
  };

  /** Возвращает true только если ответ дошёл и оказался успешным */
  private _submit = async (
    stage: LoadingStageModel,
    endpoint: string,
    data?: ManagerSegmentCompleteServer
  ): Promise<boolean> => {
    if (this.isSubmitting || !this.managerId) {
      return false;
    }

    this.abortController = new AbortController();
    stage.loading();
    const response = await this.rootStore.networkStore.api(endpoint, {
      method: 'POST',
      config: { signal: this.abortController.signal },
      ...(data ? { data } : {})
    });

    if (response.isError) {
      // reset() прерывает запрос при смене сессии; стадии уже сброшены там же
      if (!response.isCancelled) {
        stage.error();
      }

      return false;
    }

    stage.success();
    return true;
  };

  private buildAnswers(): IManagerSegmentAnswer[] {
    return this.questions.map(({ question }) => ({
      question,
      values: this.answers.get(question) || []
    }));
  }

  private validateSegmentStep(): boolean {
    if (!this.isSegmentOtherRequired) {
      return Boolean(this.segment.value);
    }

    return this.validateOtherText(this.segmentOtherText);
  }

  private validateTaskOtherText(): boolean {
    return this.isTaskOtherRequired
      ? this.validateOtherText(this.taskOtherText)
      : true;
  }

  private validateCurrentTaskOtherText(): boolean {
    return this.isOtherSelected
      ? this.validateOtherText(this.taskOtherText)
      : true;
  }

  private validateComplete(): boolean {
    return this.validateSegmentStep() && this.validateTaskOtherText();
  }

  /** Отличается от isOtherTextValid только тем, что показывает ошибку в поле */
  private validateOtherText(field: FieldModel<string>): boolean {
    return field.validate() === null;
  }

  private _resetForm = (): void => {
    this.step = ManagerSegmentStep.segment;
    this.questionIndex = 0;
    this.answers.clear();
    this.segment.changeValue(null);
    this.segment.resetTouched();
    this.segmentOtherText.changeValue('');
    this.segmentOtherText.resetTouched();
    this.taskOtherText.changeValue('');
    this.taskOtherText.resetTouched();
    this.skipStage.reset();
    this.completeStage.reset();
  };

  /** Отказ закрывает опрос сразу — показывать нечего */
  private _finishSkip = (): void => {
    this.modalState.close();
    this._resetForm();
  };

  /** После заполнения модалка остаётся открытой на экране благодарности */
  private _showSuccess = (): void => {
    this.rootStore.userStore.cancelAuthInfoRequest();
    this.completeStage.reset();
    this.step = ManagerSegmentStep.success;
  };
}
